import React, { useState, useEffect } from "react";
import { Table, Modal, Spinner } from "react-bootstrap";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { useSelector, useDispatch } from "react-redux";
import { baseUrl } from "../../../../services/config";

const EmployeeAttendanceProfile = () => {
  const authtoken = localStorage.getItem("userToken");
  const alert = useAlert();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const campuses = user.campuses || [];
  const campusidget = useSelector((state) => state.campuses.setselectedcampusid);

  const [campusId, setCampusId] = useState(campusidget || "");
  const [employees, setEmployees] = useState([]);
  const [rules, setRules] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [loadingRules, setLoadingRules] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [form, setForm] = useState({
    attendance_rule_id: "",
    device_user_id: "",
    remaining_leaves: "",
  });
  const [errors, setErrors] = useState({});

  const fetchRules = async () => {
    setLoadingRules(true);
    try {
      const res = await fetch(`${baseUrl}/attendance-rules`, {
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await res.json();
      if (data.metadata?.success) setRules(data.payload);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingRules(false);
    }
  };

  const fetchEmployees = async (campus) => {
    if (!campus) return;
    setLoadingEmp(true);
    try {
      const res = await fetch(`${baseUrl}/get_employee_list`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authtoken}` },
        body: JSON.stringify({ campus_id: campus, designation_ids: [], bank_account_id: "" }),
      });
      const data = await res.json();
      if (data.payload) {
        const sorted = [...data.payload].sort((a, b) => a.emp_code - b.emp_code);
        setEmployees(sorted);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingEmp(false);
    }
  };

  useEffect(() => {
    fetchRules();
    if (campusidget) {
      fetchEmployees(campusidget);
    }
  }, []);

  const openProfile = (emp) => {
    setSelectedEmp(emp);
    setForm({
      attendance_rule_id: emp.attendance_rule_id || "",
      device_user_id: emp.biometric_device_user_id || "",
      remaining_leaves: emp.remaining_leaves !== null && emp.remaining_leaves !== undefined ? emp.remaining_leaves : "",
    });
    setErrors({});
    setShowModal(true);
  };

  const handleField = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (
      form.attendance_rule_id === "" &&
      form.device_user_id === "" &&
      form.remaining_leaves === ""
    ) {
      errs.general = "Provide at least one of: Attendance Rule, Device User ID, or Remaining Leaves.";
    }
    if (form.device_user_id && !/^E\d{6}$/.test(form.device_user_id)) {
      errs.device_user_id = "Format must be E followed by 6 digits, e.g. E000010.";
    }
    if (form.remaining_leaves !== "" && (isNaN(form.remaining_leaves) || Number(form.remaining_leaves) < 0)) {
      errs.remaining_leaves = "Must be a non-negative number.";
    }
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = { employee_id: selectedEmp.id };
    if (form.attendance_rule_id !== "") payload.attendance_rule_id = form.attendance_rule_id || null;
    if (form.device_user_id !== "") payload.device_user_id = form.device_user_id;
    if (form.remaining_leaves !== "") payload.remaining_leaves = Number(form.remaining_leaves);

    setSaving(true);
    try {
      const res = await fetch(`${baseUrl}/employees/attendance-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authtoken}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.metadata?.success) {
        alert.show("Attendance profile saved successfully.");
        setShowModal(false);
        fetchEmployees(campusId);
      } else {
        const msg = data.metadata?.message;
        if (typeof msg === "object") setErrors(msg);
        else alert.error(msg || "Failed to save profile.");
      }
    } catch (err) {
      console.log(err);
      alert.error("Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = employees.filter((e) => {
    const txt = searchText.toLowerCase();
    return txt
      ? e.full_name?.toLowerCase().includes(txt) || String(e.emp_code).includes(txt)
      : true;
  });

  const getRuleName = (id) => {
    if (!id) return <span className="text-muted">—</span>;
    const r = rules.find((r) => r.id === id);
    return r ? r.name : id;
  };

  return (
    <Paper style={{ padding: 20, marginTop: 10 }}>
      <h4 className="mb-4">Employee Attendance Profile</h4>

      <div className="row mb-4">
        <div className="col-md-4">
          <label className="form-control-label"><b>Campus</b></label>
          <select
            className="form-control"
            value={campusId}
            onChange={(e) => {
              const id = e.target.value;
              setCampusId(id);
              setSearchText("");
              dispatch({
                type: "SELECTED_CAMPUS_ID",
                payload: { setselectedcampusid: id },
              });
              fetchEmployees(id);
            }}
          >
            <option value="">Select Campus</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-control-label"><b>Search</b></label>
          <input
            className="form-control"
            placeholder="Search by name or code..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {loadingEmp ? (
        <div className="text-center my-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <Table responsive bordered hover>
          <thead className="tablehead">
            <tr className="tableheadtr text-center">
              <th className="background-color-primary tableHEadNAme">#</th>
              <th className="background-color-primary tableHEadNAme">Emp Code</th>
              <th className="background-color-primary tableHEadNAme">Name</th>
              <th className="background-color-primary tableHEadNAme">Attendance Rule</th>
              <th className="background-color-primary tableHEadNAme">Device User ID</th>
              <th className="background-color-primary tableHEadNAme">Remaining Leaves</th>
              <th className="background-color-primary tableHEadNAme">Action</th>
            </tr>
          </thead>
          <tbody>
            {!campusId ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  Select a campus to view employees.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">No employees found.</td>
              </tr>
            ) : (
              filtered.map((emp, i) => (
                <tr key={emp.id}>
                  <td>{i + 1}</td>
                  <td>{emp.emp_code}</td>
                  <td>{emp.full_name}</td>
                  <td>{getRuleName(emp.attendance_rule_id)}</td>
                  <td>
                    {emp.biometric_device_user_id ? (
                      <span style={{ background: "#e8f4fd", padding: "2px 8px", borderRadius: 4, fontSize: 12 }}>
                        {emp.biometric_device_user_id}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {emp.remaining_leaves !== null && emp.remaining_leaves !== undefined
                      ? emp.remaining_leaves
                      : <span className="text-muted">—</span>}
                  </td>
                  <td className="text-center">
                    <button onClick={() => openProfile(emp)} className="btn btn-sm btn-primary">
                      Set Profile
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Attendance Profile — {selectedEmp?.full_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors.general && (
            <div className="alert alert-danger py-2">{errors.general}</div>
          )}

          <div className="mb-3">
            <label className="form-control-label"><b>Attendance Rule</b></label>
            <select
              className="form-control"
              value={form.attendance_rule_id}
              onChange={(e) => handleField("attendance_rule_id", e.target.value)}
            >
              <option value="">— No Rule —</option>
              {rules.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-control-label"><b>Biometric Device User ID</b></label>
            <input
              className={`form-control ${errors.device_user_id ? "is-invalid" : ""}`}
              value={form.device_user_id}
              onChange={(e) => handleField("device_user_id", e.target.value.toUpperCase())}
              placeholder="E000001"
              maxLength={7}
            />
            <small className="text-muted">Format: E followed by 6 digits (e.g. E000010)</small>
            {errors.device_user_id && <div className="invalid-feedback">{errors.device_user_id}</div>}
          </div>

          <div className="mb-2">
            <label className="form-control-label"><b>Remaining Leaves</b></label>
            <input
              type="number"
              min={0}
              className={`form-control ${errors.remaining_leaves ? "is-invalid" : ""}`}
              value={form.remaining_leaves}
              onChange={(e) => handleField("remaining_leaves", e.target.value)}
              placeholder="e.g. 12"
            />
            {errors.remaining_leaves && <div className="invalid-feedback">{errors.remaining_leaves}</div>}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={handleSave} disabled={saving} className="btn btn-success button_blue">
            {saving ? <Spinner size="sm" animation="border" /> : "Save"}
          </button>
          <button onClick={() => setShowModal(false)} className="btn btn-danger btn_width">
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
};

export default EmployeeAttendanceProfile;
